import { useDialogFocus } from '@shared/hooks/useDialogFocus';
import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery } from '@shared/supabase/hooks';
import { IgLink } from '@shared/ui/IgLink';

// Media-kit-style card for a collab candidate (any provider), fetched via creator_peer_card
// (creator-scoped, works for claimed AND scraped-only profiles). Opened from the collab list.
interface Kit {
  handle: string; full_name: string | null; avatar: string | null; verified: boolean | null; on_adamar: boolean | null;
  city: string | null; country: string[] | null; category: string | null; bio: string | null;
  languages: string[] | null; accepts_barter: boolean | null; niches: string[];
  followers: number | null; engagement_rate: number | null; avg_likes: number | null;
  avg_comments: number | null; median_reel_views: number | null; posts: string[];
}

const fmtK = (n: number | null | undefined) =>
  n == null ? '—' : n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${Math.round(n / 1e3)}K` : String(Math.round(n));

export default function CreatorCardModal({ providerId, handle, onClose }: { providerId: string; handle: string; onClose: () => void }) {
  const { t } = useTranslation('provider');
  const tr = (k: string, d: string) => t(k, { defaultValue: d });
  const { data, isLoading } = useSupabaseQuery<Kit | null>(['peer-card', providerId],
    async () => supabase.rpc('creator_peer_card', { p_provider_id: providerId }));

  const panelRef = useDialogFocus(onClose);

  const k = data ?? null;
  const stats = k ? [
    { label: tr('creator.card.followers', 'Подписчики'), value: fmtK(k.followers) },
    { label: tr('creator.card.er', 'Вовлечённость'), value: k.engagement_rate != null ? `${Number(k.engagement_rate).toFixed(1)}%` : '—' },
    { label: tr('creator.card.reelViews', 'Просмотры Reels'), value: fmtK(k.median_reel_views) },
    { label: tr('creator.card.avgLikes', 'Ср. лайки'), value: fmtK(k.avg_likes) },
    { label: tr('creator.card.avgComments', 'Ср. комментарии'), value: fmtK(k.avg_comments) },
  ] : [];

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '5vh 14px', overflowY: 'auto' }}>
      <div ref={panelRef} role="dialog" aria-modal="true" aria-label={`@${handle}`} onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 460, outline: 'none', background: '#12161f', border: '1px solid #232c3b', borderRadius: 16, overflow: 'hidden', position: 'relative' }}>
        <button type="button" onClick={onClose} aria-label={tr('creator.close', 'Закрыть')}
          style={{ position: 'absolute', top: 8, right: 8, zIndex: 2, background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.15)', color: 'var(--text)', width: 44, height: 44, borderRadius: 8, cursor: 'pointer', fontSize: 18, lineHeight: 1, fontFamily: 'inherit' }}>×</button>

        {isLoading ? (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--muted)' }}>…</div>
        ) : !k ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)', fontSize: 14 }}>{tr('creator.card.empty', 'Карточка недоступна.')}</div>
        ) : (
          <>
            <div style={{ padding: 20, display: 'flex', gap: 14, alignItems: 'center', borderBottom: '1px solid #1c2431' }}>
              <a href={`https://instagram.com/${k.handle}`} target="_blank" rel="noreferrer" title={`instagram.com/${k.handle}`} style={{ flex: 'none', display: 'block' }}>
                {k.avatar
                  ? <img src={k.avatar} alt="" style={{ width: 70, height: 70, borderRadius: '50%', objectFit: 'cover', border: '2px solid #2a3548', display: 'block' }} />
                  : <div style={{ width: 70, height: 70, borderRadius: '50%', background: '#1c2431' }} />}
              </a>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {k.full_name || `@${k.handle}`}
                  {k.verified && <span title="Verified" style={{ color: 'var(--accent)', fontSize: 15 }}>✔︎</span>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                  <span style={{ color: 'var(--muted)', fontSize: 14 }}>@{k.handle}</span>
                  <IgLink handle={k.handle} size={18} />
                  {!k.on_adamar && <span style={{ fontSize: 11, color: 'var(--dim)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 6, padding: '1px 6px' }}>{tr('creator.collab.notOnAdamar', 'не в AdaMar')}</span>}
                </div>
                {[k.city, k.category].filter(Boolean).length > 0 && (
                  <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 3 }}>{[k.city, k.category].filter(Boolean).join(' · ')}</div>
                )}
              </div>
            </div>

            {k.niches?.length > 0 && (
              <div style={{ padding: '12px 20px 0', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {k.niches.map((n) => (
                  <span key={n} style={{ fontSize: 12, color: 'var(--accent-text)', background: 'rgba(74,158,255,0.12)', border: '1px solid rgba(74,158,255,0.3)', borderRadius: 999, padding: '3px 10px' }}>{n}</span>
                ))}
              </div>
            )}

            <div style={{ padding: 16, display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
              {stats.map((s) => (
                <div key={s.label} style={{ background: '#0e131c', border: '1px solid #1c2431', borderRadius: 12, padding: '12px 8px', textAlign: 'center' }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text)' }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>{s.label}</div>
                </div>
              ))}
            </div>

            {(k.accepts_barter || (k.languages?.length ?? 0) > 0) && (
              <div style={{ padding: '0 20px 8px', color: 'var(--muted)', fontSize: 12.5, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                {(k.languages?.length ?? 0) > 0 && <span>{tr('creator.card.langs', 'Языки')}: {k.languages!.join(', ').toUpperCase()}</span>}
                {k.accepts_barter && <span style={{ color: '#7fd18f' }}>✓ {tr('creator.profile.barter', 'Работаю по бартеру')}</span>}
              </div>
            )}

            {k.bio && <div style={{ padding: '4px 20px 14px', color: 'var(--text-2)', fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-line' }}>{k.bio}</div>}

            {k.posts?.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 2, padding: 2 }}>
                {k.posts.slice(0, 8).map((src, i) => (
                  <div key={i} style={{ aspectRatio: '1 / 1', background: '#0e131c', overflow: 'hidden' }}>
                    <img src={src} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
